import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../api/client';
import logger from '../../utils/logger';
import './StaffManagement.css';

interface User {
    id: number;
    username: string;
    email: string;
    fullName: string;
    roles: string[];
}

interface NewUserForm {
    username: string;
    email: string;
    fullName: string;
    password: string;
    role: 'ROLE_TENANT_ADMIN' | 'ROLE_USER';
}

export const StaffManagement: React.FC = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [formData, setFormData] = useState<NewUserForm>({
        username: '',
        email: '',
        fullName: '',
        password: '',
        role: 'ROLE_USER'
    });

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        try {
            logger.debug('StaffManagement: loading users');
            const tenantId = localStorage.getItem('tenantId');
            logger.debug('StaffManagement: tenantId present?', Boolean(tenantId));

            const response = await apiFetch('/api/users');

            if (response.ok) {
                const data = await response.json();
                logger.debug('StaffManagement: users loaded', data.length);
                setUsers(data);
            } else {
                logger.error('StaffManagement: failed to load users', response.status);
            }
        } catch (error) {
            logger.error('StaffManagement: errore caricamento utenti', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            const url = editingUser
                ? `/api/users/${editingUser.id}`
                : '/api/users';

            const payload: any = {
                username: formData.username,
                email: formData.email,
                fullName: formData.fullName,
                roles: [formData.role]
            };

            // Include password solo se è una nuova creazione o se è stata modificata
            if (!editingUser || formData.password) {
                payload.password = formData.password;
            }

            const response = await apiFetch(url, {
                method: editingUser ? 'PUT' : 'POST',
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                await loadUsers();
                closeModal();
            } else {
                const error = await response.text();
                alert(`Errore: ${error}`);
            }
        } catch (error) {
            logger.error('StaffManagement: errore salvataggio utente', error);
            alert('Errore durante il salvataggio');
        }
    };

    const handleDelete = async (userId: number) => {
        if (!window.confirm('Sei sicuro di voler eliminare questo utente?')) return;

        try {
            const response = await apiFetch(`/api/users/${userId}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                await loadUsers();
            } else {
                alert('Errore durante l\'eliminazione');
            }
        } catch (error) {
            logger.error('StaffManagement: errore eliminazione utente', error);
            alert('Errore durante l\'eliminazione');
        }
    };

    const openCreateModal = () => {
        setEditingUser(null);
        setFormData({
            username: '',
            email: '',
            fullName: '',
            password: '',
            role: 'ROLE_USER'
        });
        setShowModal(true);
    };

    const openEditModal = (user: User) => {
        setEditingUser(user);
        setFormData({
            username: user.username,
            email: user.email,
            fullName: user.fullName,
            password: '',
            role: user.roles.includes('ROLE_TENANT_ADMIN') ? 'ROLE_TENANT_ADMIN' : 'ROLE_USER'
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingUser(null);
        setFormData({
            username: '',
            email: '',
            fullName: '',
            password: '',
            role: 'ROLE_USER'
        });
    };

    const getRoleLabel = (roles: string[]) => {
        if (roles.includes('ROLE_TENANT_ADMIN')) return 'Amministratore';
        if (roles.includes('ROLE_USER')) return 'Staff';
        return 'Utente';
    };

    if (loading) {
        return <div className="staff-management-container">Caricamento...</div>;
    }

    return (
        <div className="staff-management-container">
            <div className="staff-header">
                <h1>Gestione Utenti Staff</h1>
                <div className="header-actions">
                    <button
                        className="btn-secondary"
                        onClick={() => navigate('/staff-permissions')}
                    >
                        🔒 Configura Permessi
                    </button>
                    <button className="btn-primary" onClick={openCreateModal}>
                        ➕ Nuovo Utente
                    </button>
                </div>
            </div>

            <div className="users-grid">
                {users.map(user => (
                    <div key={user.id} className="user-card">
                        <div className="user-info">
                            <div className="user-avatar">
                                {user.fullName?.charAt(0) || user.username.charAt(0).toUpperCase()}
                            </div>
                            <div className="user-details">
                                <h3>{user.fullName || user.username}</h3>
                                <p className="user-email">{user.email}</p>
                                <p className="user-username">@{user.username}</p>
                                <span className={`role-badge ${user.roles.includes('ROLE_TENANT_ADMIN') ? 'admin' : 'staff'}`}>
                                    {getRoleLabel(user.roles)}
                                </span>
                            </div>
                        </div>
                        <div className="user-actions">
                            <button
                                className="btn-edit"
                                onClick={() => openEditModal(user)}
                                title="Modifica utente"
                            >
                                ✏️
                            </button>
                            <button
                                className="btn-delete"
                                onClick={() => handleDelete(user.id)}
                                title="Elimina utente"
                            >
                                🗑️
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {users.length === 0 && (
                <div className="empty-state">
                    <p>Nessun utente trovato</p>
                    <button className="btn-primary" onClick={openCreateModal}>
                        Crea il primo utente
                    </button>
                </div>
            )}

            {showModal && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingUser ? 'Modifica Utente' : 'Nuovo Utente'}</h2>
                            <button className="btn-close" onClick={closeModal}>✕</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>Username *</label>
                                <input
                                    type="text"
                                    value={formData.username}
                                    onChange={e => setFormData({ ...formData, username: e.target.value })}
                                    required
                                    disabled={!!editingUser}
                                    placeholder="username"
                                />
                            </div>

                            <div className="form-group">
                                <label>Nome Completo *</label>
                                <input
                                    type="text"
                                    value={formData.fullName}
                                    onChange={e => setFormData({ ...formData, fullName: e.target.value })}
                                    required
                                    placeholder="Mario Rossi"
                                />
                            </div>

                            <div className="form-group">
                                <label>Email *</label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                                    required
                                    placeholder="email@esempio.com"
                                />
                            </div>

                            <div className="form-group">
                                <label>Password {editingUser ? '(lascia vuoto per non modificare)' : '*'}</label>
                                <input
                                    type="password"
                                    value={formData.password}
                                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                                    required={!editingUser}
                                    placeholder="••••••••"
                                    minLength={6}
                                />
                            </div>

                            <div className="form-group">
                                <label>Ruolo *</label>
                                <select
                                    value={formData.role}
                                    onChange={e => setFormData({ ...formData, role: e.target.value as 'ROLE_TENANT_ADMIN' | 'ROLE_USER' })}
                                    required
                                >
                                    <option value="ROLE_USER">Staff</option>
                                    <option value="ROLE_TENANT_ADMIN">Amministratore</option>
                                </select>
                                <small className="form-hint">
                                    Staff: Accesso limitato al POS e gestione ordini<br />
                                    Amministratore: Accesso completo a tutte le funzionalità
                                </small>
                            </div>

                            <div className="modal-actions">
                                <button type="button" className="btn-secondary" onClick={closeModal}>
                                    Annulla
                                </button>
                                <button type="submit" className="btn-primary">
                                    {editingUser ? 'Salva Modifiche' : 'Crea Utente'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StaffManagement;
